// A trailer upload from the HQ console (spec/14 §6, F-CAT-01/-07, ADR-0028): the same three steps as a venue
// photo (`venue-profile/uploads.ts`) — reserve the trailer, PUT the bytes straight to object storage with the
// signed URL, complete it — so the video never passes through the api. The PUT goes over `XMLHttpRequest`
// rather than `fetch` because only XHR reports upload progress, which a 200 MB video needs.
//
// The types and the size cap come from the contract's `CreateGameTrailer` (vite/contract-schemas.ts), so the
// console refuses exactly what the server would, before a byte is sent.

import type { ApiClient, GameId, GameTrailer, TrailerContentType } from '@clavian/api-client';
import { $defs } from 'virtual:clavian/contract-schemas';

export type TrailerUploadFailure = 'wrongType' | 'tooLarge' | 'failed';

export class TrailerUploadError extends Error {
  override readonly name = 'TrailerUploadError';
  readonly reason: TrailerUploadFailure;
  constructor(reason: TrailerUploadFailure) {
    super(reason);
    this.reason = reason;
  }
}

export const TRAILER_TYPES: readonly string[] = (($defs['TrailerContentType']?.['enum'] ?? []) as string[]).filter((type) => type !== '');
export const TRAILER_MAX_BYTES: number = ((($defs['CreateGameTrailer']?.['properties'] ?? {}) as Record<string, Record<string, unknown>>)['bytes']?.['maximum'] ??
  Number.POSITIVE_INFINITY) as number;

/** PUT `body` to `url` with exactly `contentType`, reporting progress 0–1. Resolves whether the store took it. */
export type PutWithProgress = (url: string, contentType: string, body: Blob, onProgress: (fraction: number) => void) => Promise<boolean>;

/** The browser's PUT: XHR, for its upload progress events. */
export const xhrPut: PutWithProgress = (url, contentType, body, onProgress) =>
  new Promise((resolve) => {
    const request = new XMLHttpRequest();
    request.open('PUT', url);
    request.setRequestHeader('content-type', contentType);
    request.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) onProgress(event.loaded / event.total);
    };
    request.onload = () => resolve(request.status >= 200 && request.status < 300);
    request.onerror = () => resolve(false);
    request.onabort = () => resolve(false);
    request.send(body);
  });

/** Reserve, upload and complete one trailer. Throws `TrailerUploadError` for a refusal before or during the PUT. */
export async function uploadTrailer(
  api: ApiClient,
  id: GameId,
  title: string,
  file: File,
  onProgress: (fraction: number) => void,
  put: PutWithProgress = xhrPut,
): Promise<GameTrailer> {
  if (!TRAILER_TYPES.includes(file.type)) throw new TrailerUploadError('wrongType');
  if (file.size === 0) throw new TrailerUploadError('failed');
  if (file.size > TRAILER_MAX_BYTES) throw new TrailerUploadError('tooLarge');

  const reserved = await api.createGameTrailer({
    path: { id },
    body: { title, contentType: file.type as TrailerContentType, bytes: file.size },
  });
  // Exactly the Content-Type the signature covers, and the body whose length it covers (spec/08 §4.19).
  const sent = await put(reserved.upload.url, reserved.upload.contentType, file, onProgress).catch(() => false);
  if (!sent) throw new TrailerUploadError('failed');
  onProgress(1);
  return api.completeGameTrailer({ path: { id, trailerId: reserved.trailer.id } });
}
